import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { categories } from './writeupdata';
import API from '../../api';

function WriteUpsSection() {
    const [writeups, setWriteups] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [writeupsPerPage] = useState(3); // 3x2 grid

    useEffect(() => {
        fetchWriteups();
    }, []);

    const fetchWriteups = async () => {
        try {
            const response = await fetch(API.WRITEUPS_PUBLIC);
            const data = await response.json();
            if (data.status === 'success') {
                setWriteups(data.writeups);
            }
        } catch (error) {
            console.error('Error fetching writeups:', error);
        }
    };

    // Calculate pagination
    const indexOfLastWriteup = currentPage * writeupsPerPage;
    const indexOfFirstWriteup = indexOfLastWriteup - writeupsPerPage;
    const currentWriteups = writeups.slice(indexOfFirstWriteup, indexOfLastWriteup);
    const totalPages = Math.ceil(writeups.length / writeupsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        // Scroll to top of writeups section
        document.querySelector('.landing-page-writeups')?.scrollIntoView({ 
            behavior: 'smooth' 
        });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
    
        let items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
    
        const buttonStyle = {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            padding: '8px 16px',
            margin: '0 4px',
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
            fontWeight: '500'
        };
    
        const activeButtonStyle = {
            ...buttonStyle,
            backgroundColor: '#dc3545',
            borderColor: '#dc3545',
            boxShadow: '0 0 15px rgba(220, 53, 69, 0.5)'
        };
    
        const disabledButtonStyle = {
            ...buttonStyle,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.4)',
            cursor: 'not-allowed'
        };
    
        // Previous button
        items.push(
            <button
                key="prev"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                style={currentPage === 1 ? disabledButtonStyle : buttonStyle}
                className="custom-page-btn"
            >
                ‹
            </button>
        );
    
        // First page if not visible
        if (startPage > 1) {
            items.push(
                <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    style={buttonStyle}
                    className="custom-page-btn"
                >
                    1
                </button>
            );
            if (startPage > 2) {
                items.push(
                    <span 
                        key="ellipsis1" 
                        style={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            padding: '8px 4px',
                            cursor: 'default'
                        }}
                    >
                        ...
                    </span>
                );
            }
        }
    
        // Page numbers
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={page === currentPage ? activeButtonStyle : buttonStyle}
                    className="custom-page-btn"
                >
                    {page}
                </button>
            );
        }
    
        // Last page if not visible
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(
                    <span 
                        key="ellipsis2" 
                        style={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            padding: '8px 4px',
                            cursor: 'default'
                        }}
                    >
                        ...
                    </span>
                );
            }
            items.push(
                <button
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                    style={buttonStyle}
                    className="custom-page-btn"
                >
                    {totalPages}
                </button>
            );
        }
    
        // Next button
        items.push(
            <button
                key="next"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                style={currentPage === totalPages ? disabledButtonStyle : buttonStyle}
                className="custom-page-btn"
            >
                ›
            </button>
        );
    
        return (
            <div className="d-flex justify-content-center mt-5">
                <div className="custom-pagination d-flex align-items-center">
                    {items.map((item, index) => (
                        <div key={index} style={{ margin: '0 2px' }}>
                            {item}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <section className="landing-page-writeups">
            <Container fluid>
                <Row className="justify-content-center">
                    <Col md={10} lg={8} className="text-center mb-5">
                        <h2 className="landing-page-writeups-title high-tech-font">Write-Ups</h2>
                        <div className="landing-page-writeups-underline"></div>
                        <p className="landing-page-writeups-subtitle">Our CTF challenge solutions and guides</p>
                        {writeups.length > 0 && (
                            <p 
                                className="landing-page-writeups-count"
                                style={{
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    fontSize: '0.9rem',
                                    marginTop: '10px',
                                    fontWeight: 400
                                }}
                            >
                                Showing {indexOfFirstWriteup + 1}-{Math.min(indexOfLastWriteup, writeups.length)} of {writeups.length} writeups
                            </p>
                            )}
                    </Col>
                </Row>

                <Row className="justify-content-center g-4">
                    {currentWriteups.map((writeup) => (
                        <Col key={writeup.id} md={6} lg={4}>
                            <a 
                                href={writeup.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="landing-page-writeup-card-link"
                            >
                                <div className="landing-page-writeup-card">
                                    <div 
                                        className="landing-page-writeup-category-icon"
                                        style={{
                                            background: categories[writeup.category]?.gradient,
                                            ...(categories[writeup.category]?.special && {
                                                boxShadow: categories[writeup.category]?.glow,
                                                animation: 'pulse 2s infinite'
                                            })
                                        }}
                                    >
                                        <FontAwesomeIcon 
                                            icon={categories[writeup.category]?.icon} 
                                            className="icon"
                                        />
                                    </div>
                                    <div className="landing-page-writeup-content">
                                        <div className="landing-page-writeup-meta">
                                            <span className="landing-page-writeup-category">
                                                {writeup.category}
                                            </span>
                                            <span className="landing-page-writeup-date">
                                                {writeup.date}
                                            </span>
                                        </div>
                                        <h3 className="landing-page-writeup-title">
                                            {writeup.title}
                                        </h3>
                                        <p className="landing-page-writeup-description">
                                            {writeup.description}
                                        </p>
                                        <div className="landing-page-writeup-author">
                                            <FontAwesomeIcon icon={faUser} className="author-icon" />
                                            <span>{writeup.author}</span>
                                        </div>
                                        <div className="landing-page-writeup-footer">
                                            <span className="landing-page-writeup-ctf">
                                                {writeup.ctf}
                                            </span>
                                            <span className="landing-page-writeup-readtime">
                                                {writeup.read_time}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </Col>
                    ))}
                </Row>

                {renderPagination()}
            </Container>
        </section>
    );
}

export default WriteUpsSection;